import { CustomButton, PrimaryButtonMTV2 } from '@/components/buttons/Buttons';
import { showNotification } from '@/modules/notification/engine/notification.library';
import { Dialog, type DialogProps } from 'primereact/dialog';
import { MdArrowBack, MdDeleteForever } from 'react-icons/md';
import style from '../views/group.module.css';
import { axiosInstance } from '@/services/axios.service';
import { BaseResponse, ResponseErrorCode } from '@/common/common_utils';
import { GetGroupResponse } from './GroupInfoScreen';
import { useNavigate } from 'react-router-dom';
import { Button } from 'primereact/button';
import { TextHeadlineLarge, TextLabelLarge2 } from '@/components/text/Texts';
import { Column } from '@/components/container/Container';
import { FormProvider, useForm } from 'react-hook-form';
import IdGeneratorService from '@/services/id-generator.service';
import { EDIT_GROUP_FORM } from '@/constants/forms';
import { useEffect } from 'react';
import { VALIDATION_MESSAGES } from '@/constants/validation';
import { useQueryClient } from '@tanstack/react-query';
import CustomInputTextarea from '@/components/form-inputs/CustomInputTextarea';
import NiceModal from '@ebay/nice-modal-react';
import ConfirmModal from '../components/ConfirmModal';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { cn } from '@/components/ui/cn';
import TextInput from '@/components/inputs/TextInput';
import { InputController } from '@/components/inputs/InputController';

interface DeleteGroupRequest {
	id: number;
}

interface EditGroupRequest {
	id: number;
	name: string;
	description: string;
}

interface FormType extends Omit<EditGroupRequest, 'id'> {}

interface Props extends DialogProps {
	group: GetGroupResponse;
}

const EditGroupDialog = (props: Props) => {
	const { group, visible, onHide, ...rest } = props;
	const queryClient = useQueryClient();
	const navigate = useNavigate();

	const formName = new IdGeneratorService(EDIT_GROUP_FORM).formId;
	const form = useForm<FormType>({
		resolver: yupResolver(yup.object({
			name: yup.string().required(VALIDATION_MESSAGES.required),
			description: yup.string().required(VALIDATION_MESSAGES.required),
		})),
		mode: 'onChange',
	});

	useEffect(() => {
		form.reset({
			name: group.Name,
			description: group.Description,
		});
	}, [group, form]);

	const handleDeleteGroup = async () => {
		await NiceModal.show(ConfirmModal, {
			title: `Delete ${group.Name}?`,
			message: 'Are you sure you want to delete this group? This action cannot be undone.',
			acceptLabel: 'Delete',
			rejectLabel: 'Cancel',
			onAccept: async () => {
				try {
					const response = await axiosInstance.post<BaseResponse>(
						'/api/X4bGroup/DeleteGroup',
						{ id: group.Id } as DeleteGroupRequest,
						{ skipSuccessCheck: true }
					);

					const { Success, ErrorCode } = response.data;

					if (!Success) {
						if (ErrorCode === ResponseErrorCode.GroupNotFound || ErrorCode === ResponseErrorCode.GroupWithEmployees)
							showNotification(response.data.ErrorMessage);
						else throw Error('Failed to delete group.');
					} else {
						showNotification('Group deleted successfully.');
						onHide();
						navigate(-1);
					}
				} catch (err) {
					console.error(err);
					showNotification('Failed to delete group.');
				}
			}
		})
	}

	const handleSubmit = async (data: FormType) => {
		try {
			const response = await axiosInstance.post<BaseResponse>(
				'/api/X4bGroup/EditGroup', {
					id: group.Id,
					name: data.name,
					description: data.description,
				} as EditGroupRequest
			);

			if (!response.data.Success) {
				showNotification(response.data.ErrorMessage);
				throw Error('Failed to update group.');
			}

			showNotification('Group updated successfully.');
			onHide();
			queryClient.invalidateQueries({ queryKey: ['GroupInfoScreen.employee', group.Id] });
		} catch (err) {
			console.error(err);
			showNotification('Failed to update group.');
		}
	};

	const headerTemplate = (
		<Column className="gap-2">
			<Button
				text
				id={'back-add-group'}
				className="self-start pl-3 pr-4 py-2 gap-2 bg-transparent hover:underline focus:outline-none"
				onClick={onHide}
			>
				<MdArrowBack size={18} className="text-primary60" />
				<TextLabelLarge2 text="Back" className="text-primary60" />
			</Button>
			<TextHeadlineLarge text="Edit Group" className="text-neutral17" />
		</Column>
	);

	const footerTemplate = (
		<div className="flex justify-end gap-2">
			<CustomButton
				id="delete-group"
				className={cn(style['group-add-modal-button'], style.groupDeleteButton)}
				rounded
				onClick={handleDeleteGroup}
			>
				<MdDeleteForever size={18} className="mr-2" />
				Delete Group
			</CustomButton>
			<PrimaryButtonMTV2
				id={'edit-program'}
				onClick={form.handleSubmit(handleSubmit)}
				label="Update"
			/>
		</div>
	);

	// TODO styles
	return (
		<Dialog
			{...rest}
			onHide={onHide}
			visible={visible}
			header={headerTemplate}
			footer={footerTemplate}
			draggable={false}
			pt={{
				root: { className: cn(style['group-add-modal-top'], style['group-add-modal-bottom'], style['group-add-modal-root']) },
				header: { className: cn(style['group-add-modal-header'], style['group-add-modal-top']) },
				content: { className: cn(style['group-edit-modal-content']) },
				footer: { className: cn(style['group-add-modal-bottom'], style['group-add-modal-footer']) },
			}}
			className="w-[1136px] tablet:!w-[calc(100vw-6rem)] tablet:!max-w-[calc(100vw-6rem)]"
		>
			<FormProvider {...form}>
				<form id={formName}>
					<Column className="gap-8">
						<InputController id="name" control={form.control}>
							<TextInput
								label="Name"
								placeholder="For example, a team name or department or role."
								containerCls="w-[calc(50%-12px)] tablet:w-full"
							/>
						</InputController>

						<CustomInputTextarea
							id="description"
							control={form.control}
							formName={formName || ''}
							label="Description"
							placeholder="Briefly describe who belongs in this group."
							rows={3}
						/>
					</Column>
				</form>
			</FormProvider>
		</Dialog>
	);
};

export default EditGroupDialog;
